<template>
  <layout>
    <section>
      <div class="card h-100">
        <div class="p-md-2 p-lg-4 p-xl-5 p-sm-2 p-3">
          <div
            v-if="accessToChange"
            class="d-inline-flex justify-content-between align-items-center w-100 pb-4"
          >
            <h4 class="p-0 m-0">{{ $t('users.users') }}</h4>
            <b-button v-b-modal.addUserModal class="p-0 palign-items-center" variant="transparent">
              <div class="d-md-inline-flex d-none d-sm-none align-items-center text-primary">
                <svg-icon name="plus-circle" class="fill-icon icon-primary" size="20"></svg-icon>
                <p class="pl-2 m-0">{{ $t('users.addUsers') }}</p>
              </div>
              <div class="d-sm-none d-flex">
                <svg-icon name="plus-circle" class="fill-icon icon-primary" size="32"></svg-icon>
              </div>
            </b-button>
          </div>
          <div>
            <b-input-group class="mt-3">
              <template #prepend>
                <b-input-group-text class="border-0 pl-0 pr-1">
                  <svg-icon name="search" class="fill-icon"></svg-icon>
                </b-input-group-text>
              </template>
              <b-form-input
                v-model="search"
                class="bg-third border-0"
                :placeholder="$t('users.search')"
              ></b-form-input>
            </b-input-group>
          </div>
          <loader v-if="loading"></loader>
          <div v-else>
            <div v-if="items && items.length">
              <div v-for="([objName, users], i) in usersListMapped" :key="i">
                <b-button
                  v-if="users.length > 0"
                  v-b-toggle="'accordion-' + i"
                  variant="transparent"
                  class="text-muted text-left px-0 mt-3"
                >
                  <svg-icon
                    v-show="visibleCollapse[i]"
                    size="24"
                    name="chevron-up"
                    class="fill-icon mr-2"
                  ></svg-icon>
                  <svg-icon
                    v-show="!visibleCollapse[i]"
                    size="24"
                    name="chevron-down"
                    class="fill-icon mr-2"
                  ></svg-icon>
                  {{ objName }}
                </b-button>
                <b-collapse :id="`accordion-` + i" v-model="visibleCollapse[i]">
                  <div v-for="(item, z) in users" :key="z">
                    <div class="d-md-flex my-2 p-2 shadow-card align-items-center d-none d-sm-none">
                      <div class="d-flex col-sm-4 align-items-center">
                        <img
                          :src="item.photo"
                          alt="profile image"
                          class="profile-img img-xs rounded-circle mr-4 imgSizeXs"
                        />
                        <div class="h7">{{ item.fullName }}</div>
                      </div>
                      <div class="col-sm-4 text-muted h7">{{ item.email }}</div>
                      <div class="col-sm-3 h7">{{ groupText(item.group) }}</div>
                      <div class="col-sm-1 text-right align-self-center">
                        <UsersActionDropDown
                          v-if="accessToChange"
                          :group="item.group"
                          :email="item.email"
                          @groupChange="groupChange"
                          @disableUser="disableUser"
                        />
                      </div>
                    </div>
                    <div class="shadow-card d-block d-md-none my-2">
                      <div class="d-flex justify-content-start align-items-center px-2 mx-1">
                        <div class="py-2 my-1 pr-1 mr-2">
                          <img
                            :src="item.photo"
                            alt="profile image"
                            class="profile-img rounded-circle imgSizeXl"
                          />
                        </div>
                        <div class="d-block w-100">
                          <div class="d-flex flex-fill justify-content-between">
                            <div class="h7">{{ item.fullName }}</div>
                            <UsersActionDropDown
                              v-if="accessToChange"
                              :group="item.group"
                              :email="item.email"
                              @groupChange="groupChange"
                              @disableUser="disableUser"
                            />
                          </div>
                          <div class="h7 text-muted pb-2 wordWrap">
                            {{ maxLengthEmail(item.email) }}
                          </div>
                          <div class="text-muted h7">
                            {{ groupText(item.group) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>
            <div v-else class="no-users">
              <no-data-message :title="$t('users.noEmployees')"></no-data-message>
            </div>
          </div>
        </div>
      </div>
      <UsersInviteUserModal modal-id="addUserModal" @refresh="refresh" />
      <delete-confirmation-modal
        id="confirmationModel"
        modal-id="confirmationModel"
        @ok="deleteUser(emailForDelete)"
      />
    </section>
  </layout>
</template>

<script>
import defaultImage from '@/assets/images/samples/6.jpg';
import orgUsersQuery from '@/api/user/orgUsers.gql';
import updateUserGroupMutation from '@/api/user/mutations/updateUserGroup.gql';
import deleteUserFromOrgMutation from '@/api/user/mutations/deleteUserFromOrg.gql';
import { vueWindowSizeMixin } from 'vue-window-size';

export default {
  name: 'Users',
  components: {
    UsersActionDropDown: () => import('@/components/base/settings/Users/UsersActionDropdown'),
    UsersInviteUserModal: () => import('@/components/base/settings/Users/UsersInviteUserModal'),
    NoDataMessage: () => import('@/components/base/message/NoDataMessage'),
  },
  mixins: [vueWindowSizeMixin],
  apollo: {
    usersList: {
      query() {
        return orgUsersQuery;
      },
      // deep: true,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (
          queryData &&
          queryData.availableOrgEmployees &&
          queryData.availableOrgEmployees.length
        ) {
          const userId = this.$store.getters.userId;

          const reduced = queryData.availableOrgEmployees.reduce(function (filtered, employee) {
            if (employee.id != userId) {
              const photoUrl =
                employee.attachmentPhoto && employee.attachmentPhoto.url
                  ? employee.attachmentPhoto.url
                  : defaultImage;

              filtered.push({
                photo: photoUrl,
                fullName:
                  employee.fullName && employee.fullName.length > 1
                    ? employee.fullName
                    : employee.email.split('@')[0],
                orgName: employee.itObject.name,
                orgId: employee.itObject.id,
                group: employee.group,
                email: employee.email.toLowerCase(),
                id: employee.id.toString(),
              });
            }

            return filtered;
          }, []);
          return reduced;
        } else {
          // this.$store.commit('SET_ERROR', this.$t('box.error.equipment'))
          return null;
        }
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },
    },
  },
  data: function () {
    return {
      emailForDelete: '',
      visibleCollapse: [],
      search: '',
      collapseControl: true,
    };
  },
  computed: {
    accessToChange() {
      return this.$store.getters.isAdmin;
    },
    loading() {
      return this.$apollo.queries.usersList.loading;
    },
    items() {
      if (this.usersList && this.usersList.length) {
        return (
          this.usersList.filter(
            (el) =>
              el.fullName.toUpperCase().includes(this.search.toUpperCase()) ||
              el.email.toUpperCase().includes(this.search.toUpperCase()),
          ) || []
        );
      }
      return [];
    },
    usersListMapped() {
      return this.groupBy(this.items, (item) => item.orgName);
    },
    userOrganization() {
      return [
        {
          text: this.$store.getters.userObject,
          value: this.$store.getters.userObjectId,
        },
      ];
    },
  },
  methods: {
    refresh() {
      this.$apollo.queries.usersList.refetch();
      this.visibleValues(this.usersListMapped.size);
    },
    groupBy(list, keyGetter) {
      const map = new Map();
      list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
          map.set(key, [item]);
        } else {
          collection.push(item);
        }
      });
      if (this.collapseControl && map.size) {
        this.visibleValues(map.size);
        this.collapseControl = false;
      }
      return map;
    },
    maxLengthEmail(email) {
      let count = Math.trunc(this.windowWidth / 13);
      return email.length < count ? email : email.substring(0, count) + '...';
    },
    visibleValues(length) {
      this.visibleCollapse = [];
      for (let i = 0; i < length; i++) {
        this.visibleCollapse.push(true);
      }
    },
    groupText(group) {
      return group == 'OEE_PM' ? this.$t('roles.OEE_PM') : this.$t('roles.OEE_USER');
    },
    groupChange(email, group) {
      this.$bvToast.toast(this.$t('userTable.swal.requestMsg', [email]), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
      });
      this.$apollo
        .mutate({
          mutation: updateUserGroupMutation,
          variables: {
            login: email,
            group: group,
          },
          refetchQueries: [
            {
              query: orgUsersQuery,
            },
          ],
        })
        .then(() => {})
        .catch((e) => {
          this.$bvToast.toast(this.$t('userTable.swal.requestMsgFail', [email]), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
    disableUser(email) {
      this.emailForDelete = email;
      this.$bvModal.show('confirmationModel');
    },
    deleteUser(email) {
      this.$apollo
        .mutate({
          mutation: deleteUserFromOrgMutation,
          variables: {
            email: email,
          },
          refetchQueries: [
            {
              query: orgUsersQuery,
            },
          ],
        })
        .then(() => {
          this.$bvToast.toast(this.$t('userTable.deleteswal.successMsg', [email]), {
            title: this.$t('toast.success'),
            variant: 'success',
            solid: true,
          });
        })
        .catch((e) => {
          this.$bvToast.toast(this.$t('userTable.deleteswal.successMsgFailed', [email]), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.imgSizeXl {
  height: 80px;
  width: 80px;
}
.imgSizeXs {
  height: 32px;
  width: 32px;
}
.wordWrap {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.displayMobile {
  display: -webkit-box !important;
}
.whiteSpace {
  white-space: break-spaces !important;
}
.no-users {
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  .width-300 {
    width: 300px;
  }
}
</style>
